import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';
import React from 'react';
import { Navigate } from 'react-router-dom';
import FundOpportunityCardData from '../models/FundOpportunityCardData';
import LegacyPropertyCardAddressMap from './legacyPropertyCard/LegacyPropertyCardAddressMap';
import PropertyCardBodyContent from './propertyCard/PropertyCardBodyContent';
import PropertyCardFooter from './propertyCard/PropertyCardFooter';
import PropertyCardHeader from './propertyCard/PropertyCardHeader';
import PropertyCardImageSection from './propertyCard/PropertyCardImageSection';
import PropertyCardImageZoomModal from './propertyCard/PropertyCardImageZoomModal';
import PropertyCardVideoEmbed from './propertyCard/PropertyCardVideoEmbed';
import { CARD_MAX_HEIGHT_PX } from './propertyCard/propertyCardConstants';
import {
  PropertyCardCustom,
  PropertyCardProps,
  PropertyCardState,
  PropertyCardWithData,
} from './propertyCard/propertyCardTypes';
import {
  asContentManagement,
  asDisplayProps,
  buildCardContentHtmlForMeasurement,
  buildFinalLink,
  deriveImageUrl,
  getDescriptionFromProps,
  processWithFootnotes,
} from './propertyCard/propertyCardUtils';
import ChartRenderer from './template/ChartRenderer';
import { Palette } from 'Palette';
import {
  FLUSH_IMAGE_ONLY_CARD,
  HIDE_EMPTY_CARD_TITLE_ROW,
  PROPERTY_CARD_EQUAL_HEIGHT,
  RADIUS,
} from 'DesignTokens';
import { determineNextTone } from './styles/styles';

export type {
  PropertyCardContentManagement,
  PropertyCardCustom,
  PropertyCardProps,
  PropertyCardPropsBase,
  PropertyCardWithData,
} from './propertyCard/propertyCardTypes';

class PropertyCard extends React.Component<PropertyCardProps, PropertyCardState> {
  private cardElementRef: HTMLElement | null = null;

  constructor(props: PropertyCardProps) {
    super(props);
    this.state = {
      imageNaturalHeight: null,
      imageNaturalWidth: null,
      isHovered: false,
      clicked: false,
      expanded: false,
      inPlaceExpanded: props.forceExpanded || false,
      lockedImageUrl: null,
      derivedImageUrl: deriveImageUrl(props),
      cardHeight: null,
      wouldExceedMaxHeight: false,
      hasTruncatedText: false,
    };
  }

  componentDidMount() {
    setTimeout(() => {
      this.checkForTruncatedText();
    }, 100);
  }

  componentDidUpdate(prevProps: PropertyCardProps, prevState: PropertyCardState) {
    if (prevProps.forceExpanded !== this.props.forceExpanded) {
      if (this.props.forceExpanded !== undefined) {
        this.setState({ inPlaceExpanded: this.props.forceExpanded });
      }
    }

    const newImage = deriveImageUrl(this.props);
    if (newImage && newImage !== this.state.derivedImageUrl && !newImage.includes('coming_soon')) {
      this.setState({ derivedImageUrl: newImage });
    }

    if (this.state.imageNaturalHeight && this.state.imageNaturalWidth && !this.state.cardHeight) {
      this.measureCardHeight();
    }

    const prevDescription = getDescriptionFromProps(prevProps);
    const currentDescription = getDescriptionFromProps(this.props);
    if (prevDescription !== currentDescription) {
      setTimeout(() => {
        this.checkForTruncatedText();
      }, 100);
    }
  }

  handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight, src } = e.currentTarget;

    this.setState(
      {
        imageNaturalHeight: Math.min(naturalHeight, 500),
        imageNaturalWidth: naturalWidth,
        lockedImageUrl: src?.match(/coming_soon/) ? null : src,
      },
      () => {
        this.measureCardHeight();
      },
    );
  };

  measureCardHeight = () => {
    const { cardData, title, description, footnotes } = this.props as PropertyCardCustom &
      PropertyCardWithData;
    const contentProps = asContentManagement(this.props);
    const cardDataRecord = cardData as FundOpportunityCardData | undefined;
    const derivedTitle = cardDataRecord ? cardDataRecord.title : title;
    const derivedDescription = cardDataRecord ? cardDataRecord.description : description;

    const processedTitle = processWithFootnotes(derivedTitle, footnotes);
    const processedDescription = processWithFootnotes(derivedDescription, footnotes);

    const html = buildCardContentHtmlForMeasurement({
      processedTitle,
      processedDescription,
      derivedImage: this.state.derivedImageUrl ?? '/assets/coming_soon.jpg',
      isVideo: contentProps.isVideo,
      isExpanded: false,
      imageNaturalHeight: this.state.imageNaturalHeight,
      imageNaturalWidth: this.state.imageNaturalWidth,
    });

    const measureDiv = document.createElement('div');
    measureDiv.style.position = 'absolute';
    measureDiv.style.left = '-9999px';
    measureDiv.style.top = '-9999px';
    measureDiv.style.width = '360px';
    measureDiv.style.maxHeight = 'none';
    measureDiv.style.visibility = 'hidden';
    measureDiv.innerHTML = html;
    document.body.appendChild(measureDiv);

    const height = measureDiv.offsetHeight;
    document.body.removeChild(measureDiv);

    this.setState({
      cardHeight: height,
      wouldExceedMaxHeight: height > CARD_MAX_HEIGHT_PX,
    });
  };

  checkForTruncatedText = () => {
    if (!this.cardElementRef) return;

    const descriptionElement = this.cardElementRef.querySelector(
      '.property-card-description',
    ) as HTMLElement | null;

    if (descriptionElement) {
      const hasContent = Boolean(
        descriptionElement.textContent && descriptionElement.textContent.trim().length > 20,
      );
      const hasTruncated = Boolean(
        hasContent && descriptionElement.scrollHeight > descriptionElement.clientHeight,
      );

      this.setState({ hasTruncatedText: hasTruncated }, () => {
        this.props.onTruncationChange?.(hasTruncated);
      });
    } else {
      this.setState({ hasTruncatedText: false }, () => {
        this.props.onTruncationChange?.(false);
      });
    }
  };

  render() {
    const {
      cardData,
      title,
      description,
      actions,
      altText,
      tone = 'tone2',
      isHtmlDescription = false,
      linkable,
      linkTo,
      removable,
      onRemove,
      mini = false,
      chartData,
      footnotes,
      className,
      forceTitleSpacing = false,
      customImageRenderer,
    } = asDisplayProps(this.props);

    const contentProps = asContentManagement(this.props);
    const allocationBreakdown = this.props.allocationBreakdown;
    const { imageNaturalHeight } = this.state;

    const cardDataRecord = cardData as FundOpportunityCardData | undefined;
    const derivedTitle = cardDataRecord ? cardDataRecord.title : title;
    const derivedDescription = cardDataRecord ? cardDataRecord.description : description;

    const processedTitle = processWithFootnotes(derivedTitle, footnotes);
    const processedDescription = processWithFootnotes(derivedDescription, footnotes);

    const derivedImage = this.state.derivedImageUrl ?? '/assets/coming_soon.jpg';
    const imageAvailable = cardDataRecord ? cardDataRecord.imageDataLoaded : true;

    const isExpanded =
      this.props.forceExpanded !== undefined
        ? this.props.forceExpanded
        : this.state.inPlaceExpanded;

    const finalLink = buildFinalLink(linkable, linkTo, cardDataRecord, this.props.routePrefix);

    const hasDescriptionContent = Boolean(
      processedDescription || actions || cardDataRecord?.investment !== undefined,
    );

    const showImageSection = derivedImage && !contentProps.isVideo && !contentProps.address;
    const hasTitle = Boolean(derivedTitle?.trim());
    const flushImage = Boolean(
      FLUSH_IMAGE_ONLY_CARD &&
      showImageSection &&
      !chartData &&
      !hasDescriptionContent &&
      !hasTitle &&
      !linkable &&
      !removable,
    );
    const hideTitleRow =
      flushImage || (HIDE_EMPTY_CARD_TITLE_ROW && forceTitleSpacing && !hasTitle && !removable);

    return (
      <>
        {!contentProps.isVideo && (
          <PropertyCardImageZoomModal
            derivedImage={derivedImage}
            altText={altText}
            derivedTitle={derivedTitle}
            processedTitle={processedTitle}
            processedDescription={processedDescription}
            tone={tone}
            isHtmlDescription={isHtmlDescription}
            footnotes={footnotes}
            cardData={cardDataRecord}
            actions={actions}
            linkable={linkable}
            printMode={this.props.printMode}
            expanded={this.state.expanded}
            onClose={() => this.setState({ expanded: false })}
            onLinkClick={() => this.setState({ clicked: true })}
          />
        )}

        <Box
          className={`react-PropertyCard${this.props.printMode ? ' keep-together-for-print' : ''} ${className}`.trim()}
          ref={(el) => {
            this.cardElementRef = el as HTMLElement | null;
          }}
          sx={{
            border: '1px solid #E0E0E0',
            backgroundColor: Palette[tone].background,
            color: '#1C1C1C',
            borderRadius: mini ? RADIUS.propertyCardMini : RADIUS.propertyCard,
            overflow: 'hidden',
            width: '100%',
            maxWidth: '360px',
            maxHeight: isExpanded ? '1000px' : '30rem',
            display: 'flex',
            flexDirection: 'column',
            alignSelf: PROPERTY_CARD_EQUAL_HEIGHT ? 'stretch' : undefined,
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxSizing: 'border-box',
            // Cards without description content used to be padded out to a
            // fixed 300px floor so they didn't look tiny sitting next to
            // taller cards. Now that admin cards stretch to match the
            // tallest sibling and pin their footer to the bottom (see
            // PROPERTY_CARD_EQUAL_HEIGHT), that floor is redundant — and
            // actively harmful when a *description-bearing* card's own
            // natural height is under 300px, since it would then be forced
            // to stretch up to match this artificial minimum, reopening the
            // exact "empty gap above the footer" this system fixes.
            minHeight: !hasDescriptionContent && !PROPERTY_CARD_EQUAL_HEIGHT ? '300px' : undefined,
          }}
        >
          {removable && onRemove && (
            <Box
              sx={{
                my: '0.5rem',
                mx: '0.5rem',
                backgroundColor: Palette[determineNextTone(tone)].background,
                zIndex: 2,
              }}
              onClick={(e) => {
                e.stopPropagation();
                (onRemove as PropertyCardCustom['onRemove'])?.();
              }}
            >
              <IconButton size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          )}

          {!hideTitleRow && (
            <PropertyCardHeader
              mini={mini}
              tone={tone}
              processedTitle={processedTitle}
              derivedTitle={derivedTitle}
              footnotes={footnotes}
              forceTitleSpacing={forceTitleSpacing}
              hasUnreadUpdates={this.props.hasUnreadUpdates}
            />
          )}

          {chartData ? (
            <Box sx={{ p: mini ? 1.5 : 2, mb: mini ? 1 : 2 }}>
              <ChartRenderer data={chartData} height={300} width="100%" hideDescription={true} />
            </Box>
          ) : (
            showImageSection && (
              <PropertyCardImageSection
                derivedImage={derivedImage}
                altText={altText}
                derivedTitle={derivedTitle}
                mini={mini}
                isExpanded={isExpanded}
                imageNaturalHeight={imageNaturalHeight}
                imageAvailable={imageAvailable}
                preventImageCrop={this.props.preventImageCrop}
                printMode={this.props.printMode}
                hasDescriptionContent={hasDescriptionContent}
                flush={flushImage}
                customImageRenderer={customImageRenderer}
                onImageLoad={this.handleImageLoad}
                onToggleInPlaceExpanded={() =>
                  this.setState({ inPlaceExpanded: !this.state.inPlaceExpanded })
                }
                onExpand={this.props.onExpand}
              />
            )
          )}

          {contentProps.address && <LegacyPropertyCardAddressMap address={contentProps.address} />}

          {contentProps.isVideo &&
            (contentProps.videoURL || contentProps.libraryVideoExternalId) && (
              <PropertyCardVideoEmbed
                videoSource={contentProps.videoSource}
                videoURL={contentProps.videoURL}
                libraryVideoExternalId={contentProps.libraryVideoExternalId}
                getVideoPlaybackUrl={contentProps.getVideoPlaybackUrl}
                cardTitle={contentProps.titlePlaceholder ? undefined : contentProps.title?.trim()}
              />
            )}

          {hasDescriptionContent && (
            <PropertyCardBodyContent
              mini={mini}
              processedDescription={processedDescription}
              isHtmlDescription={isHtmlDescription}
              isExpanded={isExpanded}
              inPlaceExpanded={this.state.inPlaceExpanded}
              cardData={cardDataRecord}
              actions={actions}
              allocationBreakdown={allocationBreakdown}
            />
          )}

          <PropertyCardFooter
            mini={mini}
            processedDescription={processedDescription}
            linkable={linkable}
            expandable={this.props.expandable}
            forceExpanded={this.props.forceExpanded}
            hasTruncatedText={this.state.hasTruncatedText}
            isExpanded={isExpanded}
            onLinkClick={() => this.setState({ clicked: true })}
            onToggleExpand={() => {
              this.setState({ inPlaceExpanded: !this.state.inPlaceExpanded });
              this.props.onExpand?.();
            }}
          />
        </Box>
        {this.state.clicked && finalLink && <Navigate to={finalLink} />}
      </>
    );
  }
}

export default PropertyCard;
